import { z } from 'zod';
import { FamilyId } from '../types/branded';
import { FamilyAccountTransactionSchema } from './family-account-transaction';

export const QaTuitionBalanceResetRecipeIdSchema = z.literal(
  'restore_tuition_open_balance',
);

export const buildQaTuitionBalanceResetRollbackConfirmationText = (input: {
  familyName: string;
  academicYear: string;
}): string =>
  `ROLL BACK TUITION FOR ${input.familyName.trim().toUpperCase()} — ${input.academicYear.trim()}`;

export const QaTuitionBalanceResetPreviewInputSchema = z.object({
  orgId: z.string().uuid(),
  familyId: FamilyId,
  academicYear: z.string().min(1),
  targetOpenBalanceCents: z.number().int().nonnegative().max(50_000_000),
});

export const QaTuitionBalanceResetApplyInputSchema =
  QaTuitionBalanceResetPreviewInputSchema.extend({
    expectedBeforeStateDigest: z.string().min(1),
    confirmationText: z.string().min(1),
    idempotencyKey: z.string().min(8).max(120).regex(/^[a-zA-Z0-9._:-]+$/),
    actorId: z.string().min(1),
    actorRole: z.literal('super-administrator'),
    effectiveDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  });

export const QaTuitionBalanceResetRollbackInputSchema = z.object({
  orgId: z.string().uuid(),
  familyId: FamilyId,
  academicYear: z.string().min(1),
  rollbackToken: z.string().uuid(),
  expectedStateDigest: z.string().min(1),
  confirmationText: z.string().min(1),
  actorId: z.string().min(1),
  actorRole: z.literal('super-administrator'),
  effectiveDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export const QaTuitionBalanceResetPreviewSchema = z.object({
  recipeId: QaTuitionBalanceResetRecipeIdSchema,
  familyId: FamilyId,
  familyName: z.string().min(1),
  academicYear: z.string().min(1),
  currentBalanceCents: z.number().int(),
  totalChargesCents: z.number().int().nonnegative(),
  totalCreditsCents: z.number().int().nonnegative(),
  targetOpenBalanceCents: z.number().int().nonnegative(),
  adjustmentAmountCents: z.number().int().nonnegative(),
  adjustmentDirection: z.enum(['charge', 'credit', 'none']),
  affectedRecordCount: z.number().int().nonnegative(),
  canApply: z.boolean(),
  beforeStateDigest: z.string().min(1),
  confirmationText: z.string().min(1),
});

export const QaTuitionBalanceResetApplyResultSchema = z.object({
  status: z.enum(['applied', 'already_applied', 'no_change']),
  preview: QaTuitionBalanceResetPreviewSchema,
  ledgerEntry: FamilyAccountTransactionSchema.nullable(),
  verifiedBalanceCents: z.number().int(),
  afterStateDigest: z.string().min(1),
  rollbackToken: z.string().uuid().nullable(),
  rollbackConfirmationText: z.string().min(1).nullable(),
  idempotencyKey: z.string().min(1),
  appliedAt: z.string(),
});

export const QaTuitionBalanceResetRollbackResultSchema = z.object({
  status: z.enum(['rolled_back', 'already_rolled_back']),
  familyId: FamilyId,
  academicYear: z.string().min(1),
  originalLedgerEntryId: z.string().uuid(),
  rollbackLedgerEntry: FamilyAccountTransactionSchema.nullable(),
  verifiedBalanceCents: z.number().int(),
  afterStateDigest: z.string().min(1),
  rolledBackAt: z.string(),
});

export type QaTuitionBalanceResetPreviewInput = z.infer<
  typeof QaTuitionBalanceResetPreviewInputSchema
>;
export type QaTuitionBalanceResetApplyInput = z.infer<
  typeof QaTuitionBalanceResetApplyInputSchema
>;
export type QaTuitionBalanceResetRollbackInput = z.infer<
  typeof QaTuitionBalanceResetRollbackInputSchema
>;
export type QaTuitionBalanceResetPreview = z.infer<
  typeof QaTuitionBalanceResetPreviewSchema
>;
export type QaTuitionBalanceResetApplyResult = z.infer<
  typeof QaTuitionBalanceResetApplyResultSchema
>;
export type QaTuitionBalanceResetRollbackResult = z.infer<
  typeof QaTuitionBalanceResetRollbackResultSchema
>;
